import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';

const statusOptions = [
  { value: 'OPEN', label: 'Open' },
  { value: 'LIMITED', label: 'Limited' },
  { value: 'SOLD_OUT', label: 'Sold Out' },
  { value: 'CANCELLED', label: 'Cancelled' },
] as const;

type DepartureStatus = typeof statusOptions[number]['value'];

interface DepartureFormData {
  departureDate: string;
  returnDate: string;
  capacity: number;
  status: DepartureStatus;
  isGuaranteed: boolean;
  notes?: string;
}

interface DepartureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId: string;
  packageDuration: number;
  departure?: {
    id: string;
    departureDate: Date;
    returnDate: Date;
    capacity: number;
    bookedCount: number;
    status: DepartureStatus;
    isGuaranteed: boolean;
    notes: string | null;
  };
  onSuccess?: () => void;
}

function formatDateForInput(date: Date): string {
  return new Date(date).toISOString().split('T')[0] ?? '';
}

export function DepartureModal({
  open,
  onOpenChange,
  packageId,
  packageDuration,
  departure,
  onSuccess,
}: DepartureModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!departure;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<DepartureFormData>({
    defaultValues: departure
      ? {
          departureDate: formatDateForInput(departure.departureDate),
          returnDate: formatDateForInput(departure.returnDate),
          capacity: departure.capacity,
          status: departure.status,
          isGuaranteed: departure.isGuaranteed,
          notes: departure.notes ?? undefined,
        }
      : {
          capacity: 20,
          status: 'OPEN',
          isGuaranteed: false,
        },
  });

  // Auto-calculate return date based on departure date and package duration
  const departureDate = watch('departureDate');
  useEffect(() => {
    if (departureDate && !isEditing) {
      const depDate = new Date(departureDate);
      depDate.setDate(depDate.getDate() + packageDuration - 1);
      setValue('returnDate', formatDateForInput(depDate));
    }
  }, [departureDate, packageDuration, setValue, isEditing]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      if (departure) {
        reset({
          departureDate: formatDateForInput(departure.departureDate),
          returnDate: formatDateForInput(departure.returnDate),
          capacity: departure.capacity,
          status: departure.status,
          isGuaranteed: departure.isGuaranteed,
          notes: departure.notes ?? undefined,
        });
      } else {
        reset({
          capacity: 20,
          status: 'OPEN',
          isGuaranteed: false,
          departureDate: '',
          returnDate: '',
        });
      }
    }
  }, [open, departure, reset]);

  const utils = trpc.useUtils();

  const createMutation = trpc.package.addDeparture.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(packageId);
      reset();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const updateMutation = trpc.package.updateDeparture.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(packageId);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: DepartureFormData) => {
    setIsSubmitting(true);

    if (isEditing && departure) {
      updateMutation.mutate({
        id: departure.id,
        data: {
          departureDate: data.departureDate,
          returnDate: data.returnDate,
          capacity: data.capacity,
          status: data.status,
          isGuaranteed: data.isGuaranteed,
          notes: data.notes || undefined,
        },
      });
    } else {
      createMutation.mutate({
        packageId,
        departureDate: data.departureDate,
        returnDate: data.returnDate,
        capacity: data.capacity,
        status: data.status,
        isGuaranteed: data.isGuaranteed,
        notes: data.notes,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Departure' : 'Add Departure'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the departure information below.'
              : 'Schedule a new departure date for this package.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="departureDate">Departure Date *</Label>
              <Input
                id="departureDate"
                type="date"
                {...register('departureDate', { required: 'Departure date is required' })}
              />
              {errors.departureDate && (
                <p className="text-sm text-red-500">{errors.departureDate.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="returnDate">Return Date *</Label>
              <Input
                id="returnDate"
                type="date"
                {...register('returnDate', { required: 'Return date is required' })}
              />
              {errors.returnDate && (
                <p className="text-sm text-red-500">{errors.returnDate.message}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="capacity">Capacity *</Label>
              <Input
                id="capacity"
                type="number"
                min="1"
                {...register('capacity', {
                  required: 'Capacity is required',
                  valueAsNumber: true,
                  min: { value: 1, message: 'Capacity must be at least 1' },
                })}
              />
              {errors.capacity && (
                <p className="text-sm text-red-500">{errors.capacity.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Combobox
                options={statusOptions.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
                value={watch('status') || 'OPEN'}
                onValueChange={(value) => setValue('status', value as DepartureStatus)}
                placeholder="Select status"
                searchPlaceholder="Search..."
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isGuaranteed"
                {...register('isGuaranteed')}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="isGuaranteed" className="font-normal">
                Guaranteed Departure
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Mark this departure as guaranteed to run regardless of minimum participants
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Any special notes for this departure..."
              rows={2}
            />
          </div>

          {/* Error Messages */}
          {(createMutation.error || updateMutation.error) && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {createMutation.error?.message || updateMutation.error?.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Add Departure'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
